import { useState, useCallback, useEffect, useRef, memo } from "react";
import { ActivityIndicator } from "react-native";
import { View } from "@src/components";
import { useVideoPlayer } from "expo-video";
import { useSharedValue, useAnimatedStyle, withTiming, useAnimatedReaction, Easing } from "react-native-reanimated";
import type { Clip } from "../types/clip";
import { VideoProgressBar } from "./VideoProgressBar";
import { NoClipPlaceholder } from "./NoClipPlaceholder";
import { ClipInfoHeader } from "./ClipInfoHeader";
import { VideoThumbnailOverlay } from "./VideoThumbnailOverlay";
import { VideoControlsOverlay } from "./VideoControlsOverlay";
import { VideoPlayerView, type VideoViewRef } from "./VideoPlayerView";
import { ExitFullscreenButton } from "./ExitFullscreenButton";
import { AnimatedFullscreenButton } from "./AnimatedFullscreenButton";
import { VideoLoadingOverlay } from "./VideoLoadingOverlay";
import { StyleSheet, useUnistyles } from "react-native-unistyles";

interface ClipVideoPlayerProps {
  clip: Clip | null;
  isLoading?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  screenHeight: number;
  screenWidth: number;
  clipIndex?: number;
  totalClips?: number;
  orientation?: "portrait" | "landscape";
  selectedHour: number;
}

function formatHourRange(hour: number): string {
  const formatHour = (h: number): string => {
    if (h === 0 || h === 24) {
      return "12:00am";
    }
    if (h < 12) {
      return `${h}:00am`;
    }
    if (h === 12) {
      return "12:00pm";
    }
    return `${h - 12}:00pm`;
  };

  const startHour = formatHour(hour);
  const endHour = formatHour(hour + 1);
  return `${startHour} - ${endHour}`;
}

export const ClipVideoPlayer = memo(
  ({
    clip,
    isLoading = false,
    onPrevious,
    onNext,
    hasPrevious = false,
    hasNext = false,
    screenHeight,
    screenWidth,
    clipIndex,
    totalClips,
    orientation = "portrait",
    selectedHour,
  }: ClipVideoPlayerProps) => {
    const { theme } = useUnistyles();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(false);
    const videoViewRef = useRef<VideoViewRef>(null);
    const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isLandscape = orientation === "landscape";

    // Controls fade animation
    const controlsOpacity = useSharedValue(0);

    // Sync controlsVisible state with opacity animation
    useAnimatedReaction(
      () => controlsVisible || isFullscreen,
      (shouldShow) => {
        controlsOpacity.value = withTiming(shouldShow ? 1 : 0, {
          duration: 150,
          easing: Easing.out(Easing.ease),
        });
      },
      [controlsVisible, isFullscreen],
    );

    const controlsAnimatedStyle = useAnimatedStyle(() => ({
      opacity: controlsOpacity.value,
    }));

    const player = useVideoPlayer(clip?.videoUrl || null, (player) => {
      player.loop = false;
      player.play();
    });

    const [isPlaying, setIsPlaying] = useState(player.playing);
    const [isEnded, setIsEnded] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Reset states when clip changes
    useEffect(() => {
      setVideoLoaded(false);
      setIsPlaying(false);
      setIsEnded(false);
      setControlsVisible(false);
      setCurrentTime(0);
      setDuration(0);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, [clip?.id]);

    // Play video when clip changes
    useEffect(() => {
      if (clip?.videoUrl && player) {
        // Play after a short delay to ensure player source is updated and ready
        const timeoutId = setTimeout(() => {
          /* console.log("ClipVideoPlayer: attempting play", {
          id: clip?.id,
          url: clip?.videoUrl,
          isPlaying: player.playing,
        }); */
          if (!player.playing) {
            try {
              player.play();
            } catch (error: unknown) {
              console.warn("Failed to play video:", error);
            }
          }
        }, 300);
        return () => clearTimeout(timeoutId);
      }
    }, [clip?.id, clip?.videoUrl, player]);

    useEffect(() => {
      const playingSubscription = player.addListener("playingChange", ({ isPlaying }) => {
        setIsPlaying(isPlaying);
      });
      const endSubscription = player.addListener("playToEnd", () => {
        setIsEnded(true);
        setControlsVisible(true);
      });
      return () => {
        playingSubscription.remove();
        endSubscription.remove();
      };
    }, [player]);

    // Track playback progress
    useEffect(() => {
      // Update duration when it becomes available
      if (player.duration > 0) {
        setDuration(player.duration);
      }

      // Poll for current time while playing
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const updateProgress = () => {
        if (player.currentTime >= 0) {
          setCurrentTime(player.currentTime);
        }
        if (player.duration > 0 && duration !== player.duration) {
          setDuration(player.duration);
        }
      };

      // Update immediately
      updateProgress();

      // Poll every 100ms for smooth progress updates
      intervalId = setInterval(updateProgress, 100);

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [player, duration]);

    const handlePlayPause = useCallback(() => {
      if (isEnded) {
        // Restart from beginning
        player.currentTime = 0;
        setIsEnded(false);
        player.play();
      } else if (isPlaying) {
        player.pause();
      } else {
        player.play();
      }
    }, [isPlaying, isEnded, player]);

    const handleFullscreen = useCallback(async () => {
      if (videoViewRef.current) {
        if (isFullscreen) {
          // Exit fullscreen - let VideoView handle it naturally
          try {
            if (typeof videoViewRef.current.exitFullscreen === "function") {
              await videoViewRef.current.exitFullscreen();
            } else {
              await videoViewRef.current.enterFullscreen();
            }
          } catch (error) {
            console.warn("Failed to exit fullscreen:", error);
          }
        } else {
          // Enter fullscreen
          await videoViewRef.current.enterFullscreen();
        }
      }
    }, [isFullscreen]);

    const handleFullscreenEnter = useCallback(() => {
      setIsFullscreen(true);
      setControlsVisible(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, []);

    const handleFullscreenExit = useCallback(() => {
      setIsFullscreen(false);
      setControlsVisible(false);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, []);

    // Toggle controls on tap
    const handleVideoTap = useCallback(() => {
      // Clear existing timeout
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }

      // Toggle visibility
      const newVisible = !controlsVisible;
      // console.log("ClipVideoPlayer: Video tapped, toggling controls", { isFullscreen, controlsVisible, newVisible });
      setControlsVisible(newVisible);

      // In fullscreen, don't auto-hide. Otherwise, auto-hide after 3 seconds if showing
      if (newVisible && !isFullscreen) {
        hideControlsTimeoutRef.current = setTimeout(() => {
          // console.log("ClipVideoPlayer: Hiding controls after timeout");
          setControlsVisible(false);
          hideControlsTimeoutRef.current = null;
        }, 3000);
      }
    }, [isFullscreen, controlsVisible]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
      };
    }, []);

    const handleFirstFrameRender = useCallback(() => {
      setVideoLoaded(true);
    }, []);

    if (!clip) {
      if (isLoading) {
        return (
          <>
            <View mb="3xl" mt="lg" />
            <View
              style={[
                styles.loadingContainer,
                {
                  width: screenWidth - theme.spacing.lg * 2,
                  maxHeight: screenHeight * 0.4,
                },
              ]}
              alignItems="center"
              justifyContent="center"
            >
              <ActivityIndicator size="large" color={theme.colors.text as string} />
            </View>
          </>
        );
      }
      return (
        <NoClipPlaceholder
          screenWidth={screenWidth}
          screenHeight={screenHeight}
          hourRangeLabel={formatHourRange(selectedHour)}
        />
      );
    }

    return (
      <View style={styles.container}>
        {/* Clip info - below video */}
        {!isLandscape && clipIndex !== undefined && totalClips !== undefined && (
          <ClipInfoHeader
            hourRangeLabel={formatHourRange(selectedHour)}
            clipIndex={clipIndex}
            totalClips={totalClips}
          />
        )}
        <View
          style={[
            styles.videoContainer,
            {
              maxHeight: isFullscreen || isLandscape ? undefined : screenHeight * 0.4,
            },
          ]}
        >
          {!videoLoaded && (
            <>
              {clip.thumbnailUrl && <VideoThumbnailOverlay thumbnailUrl={clip.thumbnailUrl} />}
              <VideoLoadingOverlay />
            </>
          )}

          <VideoPlayerView
            ref={videoViewRef}
            player={player}
            isFullscreen={isFullscreen}
            isLandscape={isLandscape}
            onTap={handleVideoTap}
            onFullscreenEnter={handleFullscreenEnter}
            onFullscreenExit={handleFullscreenExit}
            onFirstFrameRender={handleFirstFrameRender}
          />

          {/* Video controls overlay with fade animation */}
          <VideoControlsOverlay
            isPlaying={isPlaying}
            isEnded={isEnded}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            controlsVisible={controlsVisible}
            isFullscreen={isFullscreen}
            animatedStyle={controlsAnimatedStyle}
            onPrevious={onPrevious}
            onNext={onNext}
            onPlayPause={handlePlayPause}
          />

          {/* Fullscreen/Exit fullscreen button - bottom right with fade animation */}
          <AnimatedFullscreenButton
            isFullscreen={isFullscreen}
            controlsVisible={controlsVisible}
            animatedStyle={controlsAnimatedStyle}
            onPress={handleFullscreen}
          />

          {/* Exit fullscreen button overlay - always visible when in fullscreen */}
          {isFullscreen && <ExitFullscreenButton onPress={handleFullscreen} />}

          {/* Progress bar at the bottom */}
          {!isFullscreen && !isLandscape && <VideoProgressBar currentTime={currentTime} duration={duration} />}
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
  },
  videoContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    position: "relative",
    alignSelf: "center",
    overflow: "hidden",
  },
  loadingContainer: {
    aspectRatio: 16 / 9,
    alignSelf: "center",
    borderRadius: theme.spacing.md,
    backgroundColor: "#64748B14",
  },
}));
